{
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

  // site.js can load more than once (app embed and app ScriptTag). Each copy
  // replaces window.crl8 with its own experience registry, so pin the first
  // loaded API or later destroy/create calls hit an empty registry.
  let pinnedApi = null

  async function getCrl8Api() {
    if (pinnedApi) return pinnedApi
    for (let attempt = 0; attempt < 200; attempt += 1) {
      const crl8 = window.crl8
      if (typeof crl8?.createExperience === 'function') {
        pinnedApi = {
          createExperience: crl8.createExperience.bind(crl8),
          destroyExperience: crl8.destroyExperience.bind(crl8),
        }
        return pinnedApi
      }
      if (typeof crl8?.ready === 'function') {
        await new Promise((resolve) => crl8.ready(resolve))
        continue
      }
      await wait(50)
    }
    return null
  }

  class BazaarVoiceGallery extends HTMLElement {
    constructor() {
      super()
      this.productSku = ''
      this.galleryId = ''
      this.container = null
      this.section = null
      this.generation = 0
      this.queue = Promise.resolve()
    }

    connectedCallback() {
      this.productSku = this.getAttribute('data-product-sku') || ''
      this.galleryId = this.getAttribute('data-gallery-id') || ''
      this.container = this.querySelector('[data-crl8-container-id]')
      this.section = this.closest('[data-ugc-gallery-section]')
      if (!this.galleryId || !this.container) return
      this.enqueue((generation) => this.render(generation, false))
    }

    disconnectedCallback() {
      this.generation += 1
      const container = this.container
      this.queue = this.queue.then(() => this.destroy(container))
    }

    updateGallery(productSku) {
      if (!productSku || productSku === this.productSku || !this.container)
        return

      this.productSku = productSku
      this.setAttribute('data-product-sku', productSku)
      this.enqueue((generation) => this.render(generation, true))
    }

    // Bazaarvoice rejects createExperience until the previous experience is
    // fully destroyed, so renders must run one at a time.
    enqueue(task) {
      const generation = ++this.generation
      this.queue = this.queue
        .then(() => {
          if (generation !== this.generation) return
          return task(generation)
        })
        .catch(() => {})
    }

    async render(generation, replace) {
      const crl8 = await getCrl8Api()
      if (!crl8 || generation !== this.generation) return

      if (replace) await this.destroy(this.container)
      if (generation !== this.generation) return

      this.container.setAttribute(
        'data-crl8-filter',
        this.productFilter(this.productSku)
      )
      const experience = await this.create(crl8)
      if (generation !== this.generation) return

      const hasContent = await this.waitForContent(experience, generation)
      if (generation !== this.generation) return
      this.setVisible(hasContent)
    }

    async create(crl8) {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        try {
          return await crl8.createExperience(this.container)
        } catch (error) {
          if (!String(error?.message).includes('already been initialized')) {
            return null
          }
          await this.destroy(this.container)
          await wait(200)
        }
      }
      return null
    }

    // destroyExperience tears the experience down but then throws
    // "Cannot read properties of undefined (reading 'destroy')".
    async destroy(container) {
      const crl8 = pinnedApi
      if (!container || !crl8) return
      try {
        await crl8.destroyExperience(container)
      } catch (error) {
        // Expected; see above.
      }
    }

    // Read the experience's store rather than the DOM; remainingResultsCount
    // is only set once the media request has finished.
    async waitForContent(experience, generation) {
      const store = experience?.store
      if (!store?.getState) return false

      for (let elapsed = 0; elapsed < 8000; elapsed += 100) {
        if (generation !== this.generation) return false
        const state = store.getState()
        if (state?.dataSource?.api?.remainingResultsCount !== undefined) {
          return (state.content?.main?.length || 0) > 0
        }
        await wait(100)
      }
      return false
    }

    // Collapse rather than display:none; the carousel measures its container
    // width when it mounts and renders no tiles at zero width.
    setVisible(visible) {
      if (!this.section || window.Shopify?.designMode) return
      this.section.toggleAttribute('data-empty', !visible)
      this.section.inert = !visible
    }

    productFilter(productSku) {
      const escaped = String(productSku)
        .replaceAll('\\', '\\\\')
        .replaceAll("'", "\\'")
      return `productId:'${escaped}'`
    }
  }

  if (!customElements.get('bazaarvoice-gallery')) {
    customElements.define('bazaarvoice-gallery', BazaarVoiceGallery)
  }
}
